/**
 * app/static/storefront/hollowlog-wantlist.js
 *
 * Shared client-side store for the Wantlist feature. A wantlist entry
 * saves a CARD's identity (set + collector number + language + finish),
 * never a Shopify product/variant ID - "save the card, not the listing"
 * is the whole point, so a saved want keeps matching after the listing
 * that caused it sells or is removed.
 *
 * Guest-only for this first version: there is no authenticated customer
 * session this backend can verify a wantlist belongs to (no App Proxy /
 * Customer Account integration exists yet), so storage is entirely
 * client-side (localStorage), matched against live inventory via
 * hollowlog-search.js's `wantlist` search filter and the standalone
 * Wantlist page's /api/wantlist-matches call. A future real-account
 * version can migrate this same identity shape server-side without
 * changing what a wantlist entry IS.
 *
 * Exposes window.HollowlogWantlist. Every method degrades safely if
 * localStorage is unavailable (private browsing, storage disabled) -
 * the feature just becomes a no-op for that session rather than
 * throwing and breaking the page it's loaded into.
 */
(function () {
  "use strict";

  const STORAGE_KEY = "hollowlog_wantlist_v1";
  const CHANGED_EVENT = "hollowlog:wantlist-changed";

  function readStore() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (error) {
      return {};
    }
  }

  function writeStore(store) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
      return true;
    } catch (error) {
      return false;
    }
  }

  // A holo and a non-holo print of the same collector number are
  // different cards to a collector, so finish is part of the identity.
  // Condition/grading deliberately are NOT - a want keeps matching any
  // condition/grade of the same card (see routes_search.py's
  // _wantlist_item_clause for the matching half of this contract).
  function deriveFinish(doc) {
    if (doc.is_holo) return "holo";
    if (doc.is_reverse_holo) return "reverse_holo";
    return "non_holo";
  }

  // Must exactly match the encoding routes_search.py's
  // _parse_wantlist_items() expects: fields joined by "::" within an
  // item, items joined by "|" across a wantlist. Values are used
  // verbatim (never re-typed by a shopper) since they're always read
  // straight off an already-returned search result doc, so exact
  // catalog casing is preserved automatically.
  function keyFor(identity) {
    return [identity.set_name, identity.card_number, identity.language, identity.finish].join("::");
  }

  function identityFromDoc(doc) {
    return {
      set_name: doc.set_name || "",
      card_number: doc.card_number || "",
      language: doc.language || "",
      finish: deriveFinish(doc),
    };
  }

  function dispatchChanged() {
    try {
      window.dispatchEvent(new CustomEvent(CHANGED_EVENT));
    } catch (error) {
      // CustomEvent constructor missing entirely would be an extremely
      // old browser - degrade to no live update rather than throwing.
    }
  }

  function getAll() {
    const store = readStore();
    return Object.keys(store).map((key) => store[key]);
  }

  function count() {
    return Object.keys(readStore()).length;
  }

  function has(doc) {
    const store = readStore();
    return Boolean(store[keyFor(identityFromDoc(doc))]);
  }

  function add(doc) {
    if (!doc || !doc.set_name || !doc.card_number) return false;

    const identity = identityFromDoc(doc);
    const key = keyFor(identity);
    const store = readStore();

    store[key] = Object.assign({}, identity, {
      key: key,
      display_title: doc.display_title || doc.title || "",
      image_url: doc.image_url || "",
      // The listing that prompted the save - a convenience fallback
      // link only, never re-used for matching (see module docblock).
      added_from_url: doc.url || "",
      added_at: Date.now(),
    });

    const wrote = writeStore(store);
    if (wrote) dispatchChanged();
    return wrote;
  }

  function removeByKey(key) {
    const store = readStore();
    if (!store[key]) return false;

    delete store[key];
    const wrote = writeStore(store);
    if (wrote) dispatchChanged();
    return wrote;
  }

  function remove(doc) {
    return removeByKey(keyFor(identityFromDoc(doc)));
  }

  function toggle(doc) {
    return has(doc) ? (remove(doc), false) : (add(doc), true);
  }

  // Builds the compact `wantlist` query-param value for
  // hollowlog-search.js's "My Wantlist" filter (see
  // FILTERABLE_PARAMS/_wantlist_filter_clause). Empty string means
  // "nothing to filter on" - callers should treat that as no filter,
  // not an error.
  function encodeForUrlParam() {
    return getAll().map((entry) => keyFor(entry)).join("|");
  }

  // Builds the request body for POST /api/wantlist-matches (the
  // standalone Wantlist page's own endpoint, unbounded by URL length).
  function toApiItems() {
    return getAll().map((entry) => ({
      set_name: entry.set_name,
      card_number: entry.card_number,
      language: entry.language,
      finish: entry.finish,
    }));
  }

  window.HollowlogWantlist = {
    CHANGED_EVENT: CHANGED_EVENT,
    keyFor: function (doc) { return keyFor(identityFromDoc(doc)); },
    getAll: getAll,
    count: count,
    has: has,
    add: add,
    remove: remove,
    removeByKey: removeByKey,
    toggle: toggle,
    encodeForUrlParam: encodeForUrlParam,
    toApiItems: toApiItems,
  };
})();
